import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ExperimentRepository } from "../../domain/interfaces/ExperimentRepository";
import { ExperimentCreateInput, ExperimentStatus } from "../../domain/experiment.entity";
import { experimentsQueryKeys } from "../../infrastructure/cache/ExperimentsCacheStrategy";

export function useExperimentMutations(propertyTicker: string, repository: ExperimentRepository) {
  const queryClient = useQueryClient();

  // Used by create/softDelete, whose port methods return either a defensively-unvalidated entity
  // (create) or nothing at all (softDelete) — see Business Rule 5 for why both still invalidate
  // rather than seed the cache directly.
  const invalidateAfterWrite = async (id?: string) => {
    await queryClient.invalidateQueries({ queryKey: experimentsQueryKeys.lists() });
    if (id) {
      await queryClient.invalidateQueries({
        queryKey: experimentsQueryKeys.detail(propertyTicker, id),
      });
      // Results queries are invalidated alongside the detail query too, since a create/softDelete
      // can affect which experiments' results screens are even reachable (AC1/AC2 relevance).
      await queryClient.invalidateQueries({ queryKey: experimentsQueryKeys.results() });
    }
  };

  // Resolved: changeStatus's port method now returns Promise<ExperimentEntity> (US-002), so its
  // detail query can be patched directly instead of invalidated. The list and results queries
  // still can't be patched from a single updated entity, so they're still invalidated.
  const invalidateListsAndResults = async () => {
    await queryClient.invalidateQueries({ queryKey: experimentsQueryKeys.lists() });
    await queryClient.invalidateQueries({ queryKey: experimentsQueryKeys.results() });
  };

  const create = useMutation({
    mutationFn: (data: ExperimentCreateInput) => repository.create(data),
    onSuccess: experiment => invalidateAfterWrite(experiment.id),
    onError: error => console.error("Error creating experiment", error),
  });

  const changeStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: ExperimentStatus }) =>
      repository.changeStatus(id, status),
    onSuccess: (updatedExperiment, { id }) => {
      // Optimistic cache update: write the fresh, backend-persisted entity directly, no refetch.
      queryClient.setQueryData(experimentsQueryKeys.detail(propertyTicker, id), updatedExperiment);
      return invalidateListsAndResults();
    },
    onError: error => console.error("Error changing experiment status", error),
  });

  const softDelete = useMutation({
    mutationFn: (id: string) => repository.softDelete(id),
    onSuccess: (_result, id) => invalidateAfterWrite(id),
    onError: error => console.error("Error deleting experiment", error),
  });

  return {
    create: create.mutateAsync,
    changeStatus: changeStatus.mutateAsync,
    softDelete: softDelete.mutateAsync,
    // Resolved: isPending exposed per mutation, following useUnitaryProductMutations's exact
    // `is<Verb>ing: <mutation>.isPending` naming convention — lets US-010/US-017 drop their local
    // useState pending-state workarounds.
    isCreating: create.isPending,
    isChangingStatus: changeStatus.isPending,
    isDeleting: softDelete.isPending,
  };
}
